import { HttpEvent, HttpResponse, HttpEventType, HttpProgressEvent } from "@angular/common/http";
import { BackendPaths } from "@common/backend-paths";
import { MyLog } from "@common/common";
import { InputFieldNames } from "@common/input-definitions";
import { DocumentUploadEvent } from "@data/data.module.types";
import { DataService } from "@data/data.service";
import { dateToStr, validateDate, ValidateDateResult } from "@util/date-utils";
import { validateLength } from "@util/input-utils";
import { createValidationError, isAnyString, parseDec, trimString, ValidationError } from "@util/utils";
import { Observable, of } from "rxjs";
import { map } from "rxjs/operators";
import { OrderInputSizes } from "./order-basic";

export interface OrderSaveParams {
  data: any;
  montage_loctype: number;
  teamId: any;
  url: string;
}

export interface OrderSaveResult {
  validationErrors: ValidationError[];
  serverResult: any;
}

type DocumentUploadType = 'doc' | 'image' | 'video';

export interface DocumentUploadParams {
  orderId: string | number;
  type: DocumentUploadType;
  doctype?: string;
  file?: any;
  fileName?: string;
  //
  image?: any;
  //
  videoType?: string;
  videoData?: any;
  convert?: any;
  progressCallback?: (event: DocumentUploadEvent) => void
}

export interface DocumentUploadResult {
  success: boolean;
  errors?: any[];
  msg?: string;
  messages?: string[];
  id?: any;
}

interface FormDataLike {
  append(name: string, value: any): void;
}

class ObjectFormDataLike implements FormDataLike {
  constructor(public obj: any) { }
  append(name: string, value: any) {
    this.obj[name] = value;
  }
}

export class OrderDataHandler {
  constructor(public dataService: DataService) { }

  static create(dataService: DataService) {
    return new OrderDataHandler(dataService);
  }

  uploadDocument(params: DocumentUploadParams): Promise<DocumentUploadResult> {
    const orderId = params.orderId;

    const url = '/montage/documents/' + orderId + '/upload';

    let reqParams: any;

    const defaultValues = (fd: FormDataLike) => {
      fd.append("type", params.type);
      params.doctype && fd.append('doctype', params.doctype);
      params.fileName && fd.append("fileName", params.fileName);
      params.convert && fd.append('convert_json', JSON.stringify(params.convert));
    };

    switch (params.type) {
      case 'doc':
        if (!params.file) {
          return Promise.resolve({
            success: false,
            errors: ['Keine Datei ausgewählt']
          });
        }
        reqParams = new FormData();
        defaultValues(reqParams);

        reqParams.append("file", params.file);
        break;
      case 'image':
        reqParams = {};
        const reqParamsForm = new ObjectFormDataLike(reqParams);

        defaultValues(reqParamsForm);

        reqParamsForm.append("image", params.image);
        break;

      case 'video':
        reqParams = new FormData();
        defaultValues(reqParams);

        reqParams.append("videotype", params.videoType);
        reqParams.append("videodata", params.videoData);
        break;
    }

    if (reqParams) {
      const requestOptions: any = {
        headers: {}
      };

      if (params.progressCallback) {
        requestOptions.reportProgress = true;
        requestOptions.observe = 'events';
        requestOptions.responseType = 'json';
      }

      function isHttpResponse<T>(event: HttpEvent<T>): event is HttpResponse<T> {
        return event && event.type === HttpEventType.Response
      }

      function isHttpProgressEvent(event: HttpEvent<unknown>): event is HttpProgressEvent {
        return event && (event.type === HttpEventType.DownloadProgress
          || event.type === HttpEventType.UploadProgress);
      }

      return new Promise((resolve: (value: DocumentUploadResult) => void) => {
        let resolved = false;
        let resOutCount = 0;
        this.dataService.post(url, reqParams, requestOptions).subscribe((res: any) => {
          const doPrint = ((resOutCount % 10 === 0) || (!res) || (res && res.type !== 1));

          doPrint && MyLog.log('UP-RES:', res);

          ++resOutCount;

          if (params.progressCallback) {
            if (!res && !resolved) {
              resolve({
                success: false,
                errors: ['Unbekannter Fehler beim Speichern der Datei']
              });
              resolved = true;
              return
            }

            if (isHttpProgressEvent(res) && params.progressCallback) {
              let val = res.loaded + ' ' + res.total;

              params.progressCallback({
                type: 'IN_PROGRESS',
                value: val,
                loaded: res.loaded,
                total: res.total
              });
              return;
            }

            if (!isHttpResponse(res)) {
              params.progressCallback({
                type: 'PENDING',
                value: null
              });
              return;
            }
          }

          if (resolved) {
            return;
          }

          if (params.progressCallback) {
            params.progressCallback({
              type: 'DONE',
              value: null
            });

            res = res.body;
          }

          MyLog.log('POST:RES:', res);

          resolved = true;

          const result = res?.result;

          if (result?.success) {
            const msg = result.msg || 'Datei erfolgreich gespeichert';
            resolve({
              success: true,
              msg,
              id: result.doc_id
            });
            return;
          }
          const errors: any[] = Array.isArray(result?.errors)
            ? result.errors
            : (
              result?.error
                ? [result.error]
                : ['Unbekannter Fehler beim Speichern der Datei']
            );

          resolve({
            success: false,
            errors
          });
        });
      });
    }

    return Promise.resolve({
      success: false,
      errors: ['Implementierungsfehler. Bitte Entwickler kontaktieren.']
    });
  }

  closeOrder(order: any) {
    const url = BackendPaths.ORDERS_FULL + order.orderId;
    const data = {
      op: 'closeorder',
      deductible_customer_status:order.deductible_customer_status
    }
    return this.dataService.post(url, data);
  }

  save(params: OrderSaveParams, fieldSizes: OrderInputSizes): Observable<OrderSaveResult> {
    const d = params.data;

    console.log('SAVE:', params);

    const data: any = {
      customer_id: d.customer_id,
      partner_id: d.partner_id,
      status: d.status,
      montage_loctype: params.montage_loctype
    };

    const s = trimString;
    const l = validateLength;

    data.car_description = s(d.car_description);
    data.damage = s(d.damage);
    data.damage_location = s(d.damage_location);
    data.damage_nr = s(d.damage_nr);
    data.halter = s(d.halter);
    data.deductible = s(d.deductible);
    data.deductible_type = d.deductible_type;
    data.deductible_customer_status = d.deductible_customer_status;
    data.deductible_customer_part = s(d.deductible_customer_part);
    data.insurance = s(d.insurance);
    data.kind = s(d.kind);
    data.km = s(d.km);
    data.articlenr = s(d.articlenr);

    data.plate = s(params.data.plate);

    data.team_user_id = params.teamId;

    const errors: ValidationError[] = [];

    const e = (name: string, error: string) => {
      const err = createValidationError(name, error);
      errors.push(err);
    }
    function notSet(s: string) {
      return s + ' ist nicht gesetzt';
    }
    function tooLong(s: string) {
      return s + ' ist zu lang';
    }

    function checkAndSet(
      checkData: any,
      fieldName: string,
      title: string,
      length: any
    ) {
      if (!checkData) {
        e(fieldName, notSet(title));
      } else if (!l(checkData, length)) {
        e(fieldName, tooLong(title));
      }
    }

    if (!data.customer_id) {
      e(InputFieldNames.ORDER_CUSTOMER_ID, notSet('Kunde'));
    }

    if (data.halter && !l(data.halter, fieldSizes.halterLength)) {
      e(
        InputFieldNames.ORDER_HALTER,
        tooLong('Text für abweichenden Halter')
      );
    }

    if (!data.partner_id) {
      e(InputFieldNames.ORDER_PARTNER_ID, notSet('Partner'));
    }

    checkAndSet(
      data.plate,
      InputFieldNames.ORDER_PLATE,
      'Kennzeichen',
      fieldSizes.plateLength
    );

    let txt = 'Kilometerstand';
    let fieldName = InputFieldNames.ORDER_KM;
    if (!data.km) {
      e(fieldName, notSet(txt));
    } else {
      if (!Number.isFinite(data.km)) {
        const val = parseDec(data.km as string);
        if (isNaN(val)) {
          e(fieldName, txt + ' ist falsch');
        }
      }
    }

    if (data.kind && !l(data.kind, fieldSizes.carKindLength)) {
      e(InputFieldNames.ORDER_CAR_KIND, tooLong('Fahrzeugart'));
    }

    if (data.montage_loctype == 2) {
      data.montage_address = s(d.montage_address);
      data.montage_city = s(d.montage_city);
      data.montage_zip = s(d.montage_zip);

      checkAndSet(
        data.montage_address,
        InputFieldNames.ORDER_MONTAGE_ADDRESS,
        'Montageort(Straße)',
        fieldSizes.addressLength
      );

      if (data.montage_zip && !l(data.montage_zip, fieldSizes.zipLength)) {
        e(InputFieldNames.ORDER_MONTAGE_ZIP, tooLong('Montageort(PLZ)'));
      }

      checkAndSet(
        data.montage_city,
        InputFieldNames.ORDER_MONTAGE_CITY,
        'Montageort(Stadt)',
        fieldSizes.cityLength
      );
    }

    let res: ValidateDateResult = validateDate(d.first_reg);

    if (res.invalid || res.notSet) {
      e(InputFieldNames.ORDER_FIRST_REG, 'Erstzulassung ist nicht gesetzt oder ungültig');
    } else {
      data.first_reg = res.value;
    }

    checkAndSet(
      data.car_description,
      InputFieldNames.ORDER_CAR_DESCRIPTION,
      'Fahrzeugbeschreibung',
      fieldSizes.carDescLength
    );

    res = validateDate(d.damage_day);

    if (res.invalid) {
      e(InputFieldNames.ORDER_DAMAGE_DAY, 'Schadentag ungültig');
    } else if (res.notSet) {
      data.damage_day = '';
    } else {
      data.damage_day = res.value;
    }

    checkAndSet(
      data.damage_location,
      InputFieldNames.ORDER_DAMAGE_LOCATION,
      'Schadenort',
      fieldSizes.damageLocLength
    );

    if (data.damage_nr && !l(data.damage_nr, fieldSizes.damageNrLength)) {
      e(InputFieldNames.ORDER_DAMAGE_NR, notSet('Schadenort'));
    }

    checkAndSet(
      data.insurance,
      InputFieldNames.ORDER_INSURANCE,
      'Versicherungsscheinnummer',
      fieldSizes.insuranceLength
    );

    if (!data.deductible) {
      e(InputFieldNames.ORDER_DEDUCTIBLE, notSet('Selbstbeteiligung'));
    }

    checkAndSet(
      data.damage,
      InputFieldNames.ORDER_DAMAGE,
      'Schadenhergang',
      fieldSizes.damageLength
    );

    res = validateDate(d.work_day);

    if (res.invalid) {
      e(InputFieldNames.ORDER_WORK_DAY, 'Leistungsdatum ungültig');
    } else if (res.notSet) {
      data.work_day = '';
    } else {
      data.work_day = res.value;
    }

    data.work_from = this.toRequestTime(params.data.work_from);
    data.work_to = this.toRequestTime(params.data.work_to);

    const dateFields = ['first_reg', 'damage_day', 'work_day'];

    for (let d of dateFields) {
      const val = data[d];
      if (val instanceof Date) {
        data[d] = dateToStr(val)
      }
    }

    if (errors.length > 0) {
      return of({
        validationErrors: errors,
        serverResult: null
      });
    } else {
      return this.dataService.post(params.url, data).pipe(map((res: any) => {
        return {
          validationErrors: [],
          serverResult: res
        } as OrderSaveResult;
      }));
    }
  }

  toRequestTime(val: any) {
    val = trimString(val);
    return isAnyString(val)?val.replace(':', ''):val;
  }
}
